import { useMemo, useState } from "react";
import { FileDown, ReceiptText } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";

import { useBranch } from "@/context/branch-context";
import { useLocalization } from "@/context/localization-context";
import { BILLING_UNIT_PRICE_CZK, BILLING_VAT_RATE } from "@/config/billing";
import {
  buildMonthBounds,
  useBillingCaptureCounts,
} from "@/billing/use-billing-capture-counts";
import {
  buildInvoiceLineItems,
  computeInvoiceTotals,
  downloadInvoice,
  generateInvoice,
} from "@/billing/invoice-generator";
import { peekNextInvoiceNumber } from "@/billing/invoice-number";

const currentYearMonth = (): { year: number; monthIndex: number } => {
  const now = new Date();
  return { year: now.getFullYear(), monthIndex: now.getMonth() };
};

const formatMonthInput = (year: number, monthIndex: number): string =>
  `${year}-${String(monthIndex + 1).padStart(2, "0")}`;

const parseMonthInput = (value: string): { year: number; monthIndex: number } => {
  const [yearStr, monthStr] = value.split("-");
  const year = Number(yearStr);
  const monthIndex = Number(monthStr) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex)) {
    return currentYearMonth();
  }
  return { year, monthIndex };
};

export default function BillingPage() {
  const { t, tp, formatNumber, intlLocale } = useLocalization();
  /** an amount in CZK with two decimals, in the UI language ("1 234,56" / "1,234.56") */
  const formatCzk = (value: number) =>
    formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const { branches } = useBranch();

  const initial = useMemo(currentYearMonth, []);
  const [monthInput, setMonthInput] = useState<string>(() =>
    formatMonthInput(initial.year, initial.monthIndex),
  );
  const [selectedBranchIds, setSelectedBranchIds] = useState<string[]>([]);

  const { year, monthIndex } = useMemo(
    () => parseMonthInput(monthInput),
    [monthInput],
  );

  const bounds = useMemo(
    () => buildMonthBounds(year, monthIndex),
    [year, monthIndex],
  );

  const {
    captures,
    totalCaptures,
    isLoading,
    error,
    refetch,
  } = useBillingCaptureCounts(bounds, selectedBranchIds);

  const branchOptions = useMemo(
    () =>
      (branches ?? [])
        .filter((branch) => branch.bb_branch_id)
        .map((branch) => ({ label: branch.name, value: branch.id })),
    [branches],
  );

  const lineItems = useMemo(
    () => buildInvoiceLineItems(captures),
    [captures],
  );
  const totals = useMemo(
    () => computeInvoiceTotals(lineItems),
    [lineItems],
  );

  const previewInvoiceNumber = useMemo(
    () => peekNextInvoiceNumber(year, monthIndex),
    [year, monthIndex],
  );

  const handleDownload = () => {
    if (lineItems.length === 0) {
      return;
    }
    const generated = generateInvoice(captures, { year, monthIndex });
    downloadInvoice(generated);
    refetch();
  };

  const periodLabel = useMemo(() => {
    try {
      return new Date(year, monthIndex, 1).toLocaleDateString(intlLocale, {
        month: "long",
        year: "numeric",
      });
    } catch {
      return `${monthInput}`;
    }
  }, [intlLocale, monthInput, monthIndex, year]);

  return (
    <div className="flex flex-1 flex-col gap-4 p-4 lg:gap-6 lg:p-6">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <ReceiptText className="h-5 w-5 text-muted-foreground" />
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("billing.title")}
          </h1>
        </div>
        <p className="text-sm text-muted-foreground">
          {t("billing.description")}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("billing.periodTitle")}</CardTitle>
          <CardDescription>{t("billing.periodDescription")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="flex flex-col gap-2">
              <Label htmlFor="billing-month">{t("billing.month")}</Label>
              <Input
                id="billing-month"
                type="month"
                value={monthInput}
                onChange={(event) => setMonthInput(event.target.value)}
                className="w-full"
              />
            </div>
            <div className="flex flex-col gap-2 md:col-span-2">
              <Label>{t("billing.branches")}</Label>
              <MultiSelect
                options={branchOptions}
                selected={selectedBranchIds}
                onChange={setSelectedBranchIds}
                placeholder={t("billing.branchesPlaceholder")}
                enableSearch
                searchPlaceholder={t("controls.searchBranch")}
                clearAllLabel={t("common.clearAll")}
                noOptionsLabel={t("controls.noBranches")}
                noResultsLabel={t("controls.noBranchResults")}
                selectedCountLabel={(count) => tp("count.branches", count)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>
              {t("billing.previewTitle", { period: periodLabel })}
            </CardTitle>
            <CardDescription>{t("billing.previewDescription")}</CardDescription>
          </div>
          <div className="flex flex-col items-start gap-2 sm:items-end">
            <div className="text-xs text-muted-foreground">
              {t("billing.nextInvoiceNumber", {
                number: previewInvoiceNumber.short,
              })}
            </div>
            <Button
              onClick={handleDownload}
              disabled={lineItems.length === 0 || isLoading}
            >
              <FileDown className="mr-2 h-4 w-4" />
              {t("billing.downloadInvoice")}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("billing.columnBranch")}</TableHead>
                  <TableHead className="text-right">
                    {t("billing.columnCaptures")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("billing.columnUnitPrice")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("billing.columnSubtotal")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && captures.length === 0 ? (
                  Array.from({ length: 4 }).map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      <TableCell colSpan={4}>
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : captures.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="h-24 text-center text-muted-foreground"
                    >
                      {t("billing.noBranches")}
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {captures.map((entry) => (
                      <TableRow key={entry.branchId}>
                        <TableCell className="font-medium">
                          {entry.branchName}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatNumber(entry.captureCount)}{" "}
                          {t("billing.unitPieces")}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatCzk(BILLING_UNIT_PRICE_CZK)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatCzk(entry.captureCount * BILLING_UNIT_PRICE_CZK)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/40">
                      <TableCell className="font-semibold">
                        {t("billing.total")}
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">
                        {formatNumber(totalCaptures)}{" "}
                        {t("billing.unitPieces")}
                      </TableCell>
                      <TableCell />
                      <TableCell className="text-right font-semibold tabular-nums">
                        {formatCzk(totals.subtotalCzk)}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            {t("billing.vatNote", {
              rate: formatNumber(BILLING_VAT_RATE, { style: "percent" }),
            })}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}